import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';

const Profile: React.FC = () => {
  const { currentUser, signOut, updateProfile, updateEmail, updatePassword, error } = useAuth();
  const navigate = useNavigate();

  // Form states
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [newEmail, setNewEmail] = useState(currentUser?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI states
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'email' | 'password'>('profile');

  // Handle profile update
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) {
      setFormError('Name is required');
      return;
    }

    setFormError(null);
    setLoading(true);

    try {
      await updateProfile(displayName);
      setSuccessMessage('Profile updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle email update
  const handleEmailUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEmail.trim()) {
      setFormError('Email is required');
      return;
    }

    if (!currentPassword) {
      setFormError('Current password is required to update email');
      return;
    }

    setFormError(null);
    setLoading(true);

    try {
      await updateEmail(newEmail, currentPassword);
      setSuccessMessage(
        'Email updated successfully. Please check your new email for verification.'
      );
      setTimeout(() => setSuccessMessage(null), 5000);
      setCurrentPassword('');
    } catch (error) {
      console.error('Error updating email:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle password update
  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword) {
      setFormError('Current password is required');
      return;
    }

    if (!newPassword) {
      setFormError('New password is required');
      return;
    }

    if (newPassword.length < 6) {
      setFormError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setFormError(null);
    setLoading(true);

    try {
      await updatePassword(currentPassword, newPassword);
      setSuccessMessage('Password updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!currentUser) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">You need to be signed in to view this page</h2>
            <button
              onClick={() => navigate('/sign-in')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-dark mb-6">Account Settings</h1>

          {/* Tabs */}
          <div className="flex border-b mb-6">
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === 'profile'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-primary'
              }`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === 'email'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-primary'
              }`}
              onClick={() => setActiveTab('email')}
            >
              Email
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === 'password'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-primary'
              }`}
              onClick={() => setActiveTab('password')}
            >
              Password
            </button>
          </div>

          {/* Error message */}
          {(formError || error) && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {formError || error}
            </div>
          )}

          {/* Success message */}
          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {successMessage}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label htmlFor="displayName" className="block text-gray-700 font-medium mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="displayName"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="email-display" className="block text-gray-700 font-medium mb-2">
                  Email
                </label>
                <div
                  id="email-display"
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-700"
                >
                  {currentUser.email}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  To change your email, go to the Email tab
                </p>
              </div>

              <button
                type="submit"
                className={`px-6 py-2 ${
                  loading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                } text-white rounded-lg transition-colors`}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <form onSubmit={handleEmailUpdate} className="space-y-4">
              <div>
                <label htmlFor="newEmail" className="block text-gray-700 font-medium mb-2">
                  New Email
                </label>
                <input
                  type="email"
                  id="newEmail"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="currentPasswordEmail"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPasswordEmail"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">
                  For security, please enter your current password to change your email
                </p>
              </div>

              <button
                type="submit"
                className={`px-6 py-2 ${
                  loading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                } text-white rounded-lg transition-colors`}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Email'}
              </button>
            </form>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="block text-gray-700 font-medium mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-gray-700 font-medium mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-gray-700 font-medium mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className={`px-6 py-2 ${
                  loading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
                } text-white rounded-lg transition-colors`}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}

          {/* Sign Out Button */}
          <div className="mt-12 pt-6 border-t">
            <button
              onClick={handleSignOut}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
